import { collectVesselData, extractBayConfigurations } from './utils'
import { validateVesselData } from './validation'

/**
 * Helper functions for drawer and bay configuration management
 */
export const closeDrawer = ({
  setAccommodationBay,
  setBayConfigurations,
  setBayCount,
  setDeckMaxRange,
  setDeckMinRange,
  setDrawerType,
  setDrawerVisible,
  setEngineBay,
  setHoldMaxRange,
  setHoldMinRange,
  setLowestDeckBay,
  setMergeBays,
  setRowMaxRange,
  setRowMinRange,
  setSelectedRecord,
  setTwentiesConfigurations,
  setTwentiesCount,
}) => {
  setDrawerVisible(false)
  setDrawerType(null)
  setSelectedRecord(null)
  setBayConfigurations([])
  setBayCount(0)
  setTwentiesConfigurations([])
  setTwentiesCount(0)
  setAccommodationBay('')
  setEngineBay('')
  setLowestDeckBay('')
  setMergeBays(false)
  setDeckMaxRange('')
  setDeckMinRange('')
  setHoldMaxRange('')
  setHoldMinRange('')
  setRowMaxRange('')
  setRowMinRange('')
}

/**
 * Get drawer title based on drawer type
 */
export const getDrawerTitle = (drawerType, t) => {
  const titles = {
    clone: t('admin.drawerTitles.clone'),
    create: t('admin.drawerTitles.create'),
    update: t('admin.drawerTitles.update'),
  }

  return titles[drawerType] || ''
}

/**
 * Initialize drawer state with vessel and bay configuration data
 */
export const initializeDrawerState = ({
  drawerType,
  record,
  resetVesselIdentifiers = false,
  setAccommodationBay,
  setBayConfigurations,
  setBayCount,
  setDeckMaxRange,
  setDeckMinRange,
  setDrawerType,
  setDrawerVisible,
  setEngineBay,
  setHoldMaxRange,
  setHoldMinRange,
  setLowestDeckBay,
  setMergeBays,
  setRowMaxRange,
  setRowMinRange,
  setSelectedRecord,
  setTwentiesConfigurations,
  setTwentiesCount,
}) => {
  setDrawerType(drawerType)

  // Set selected record - reset identifiers for clone mode
  if (resetVesselIdentifiers) {
    setSelectedRecord({
      ...record,
      abbreviation: '',
      imoNumber: '',
      vesselAbbreviation: '',
      vesselName: '',
    })
  } else {
    setSelectedRecord(record)
  }

  // Extract and set bay configurations
  const bayConfig = extractBayConfigurations(record)

  setBayConfigurations(bayConfig.bayConfigurations)
  setBayCount(bayConfig.bayConfigurations.length)

  setTwentiesConfigurations(bayConfig.twentiesConfigurations)
  setTwentiesCount(bayConfig.twentiesConfigurations.length)

  setAccommodationBay(bayConfig.accommodationBay)
  setEngineBay(bayConfig.engineBay)
  setLowestDeckBay(bayConfig.lowestDeckBay)
  setMergeBays(bayConfig.mergeBays)

  // Set tier/row range values from record
  setDeckMaxRange(bayConfig.deckMaxRange)
  setDeckMinRange(bayConfig.deckMinRange)
  setHoldMaxRange(bayConfig.holdMaxRange)
  setHoldMinRange(bayConfig.holdMinRange)
  setRowMaxRange(bayConfig.rowMaxRange)
  setRowMinRange(bayConfig.rowMinRange)

  setDrawerVisible(true)
}

/**
 * Submit drawer form and perform appropriate action
 */
export const submitDrawerForm = ({
  accommodationBay,
  bayConfigurations,
  deckMaxRange,
  deckMinRange,
  drawerType,
  engineBay,
  holdMaxRange,
  holdMinRange,
  imoNumber,
  lowestDeckBay,
  mergeBays,
  onError,
  onSaveVesselDetails,
  onSuccess,
  rowMaxRange,
  rowMinRange,
  selectedRecord,
  twentiesConfigurations,
  vesselAbbreviation,
  vesselName,
}) => {
  // Merge form-edited values into selectedRecord so collectVesselData picks them up
  const mergedRecord = {
    ...selectedRecord,
    ...(vesselName !== undefined && { vesselName }),
    ...(vesselAbbreviation !== undefined && {
      abbreviation: vesselAbbreviation,
      vesselAbbreviation,
    }),
    ...(imoNumber !== undefined && { imoNumber }),
  }
  // Validate vessel data before submission
  const validation = validateVesselData({
    accommodationBay,
    bayConfigurations,
    engineBay,
    isCreateMode: drawerType === 'create' || drawerType === 'clone',
    lowestDeckBay,
    selectedRecord: mergedRecord,
    twentiesConfigurations,
  })

  if (!validation.isValid) {
    // Return error object
    return {
      error: validation.errors.join(', '),
      success: false,
    }
  }

  // Determine the operation type
  let operationType
  if (drawerType === 'create') {
    operationType = 'CREATE'
  } else if (drawerType === 'clone') {
    operationType = 'CLONE'
  } else if (drawerType === 'update') {
    operationType = 'UPDATE'
  } else {
    return {
      error: 'Invalid drawer type',
      success: false,
    }
  }

  // Collect all vessel data into a single object
  const vesselData = collectVesselData({
    accommodationBay,
    bayConfigurations,
    deckMaxRange,
    deckMinRange,
    engineBay,
    holdMaxRange,
    holdMinRange,
    lowestDeckBay,
    mergeBays,
    operationType,
    rowMaxRange,
    rowMinRange,
    selectedRecord: mergedRecord,
    twentiesConfigurations,
  })

  // Dispatch unified save action with callback
  onSaveVesselDetails({
    onError, // This callback will display the error in the drawer
    onSuccess, // This callback will close the drawer after saga success
    payload: vesselData,
    type: operationType,
  })

  return { success: true }
}

/**
 * Add a new bay configuration to Forties
 */
export const addFortyBay = ({
  bayConfigurations,
  bayCount,
  setBayConfigurations,
  setBayCount,
}) => {
  const newBay = {
    bay: '',
    fourtyFootOnly: false,
    hatchCovers: '3',
    key: bayCount,
  }
  setBayConfigurations([...bayConfigurations, newBay])
  setBayCount(bayCount + 1)
}

/**
 * Delete a bay configuration from Forties
 */
export const deleteFortyBay = ({
  bayConfigurations,
  key,
  setBayConfigurations,
}) => {
  setBayConfigurations(bayConfigurations.filter(item => item.key !== key))
}

/**
 * Save changes to a bay configuration in Forties
 */
export const saveFortyBayConfig = ({
  bayConfigurations,
  row,
  setBayConfigurations,
}) => {
  const newData = [...bayConfigurations]
  const index = newData.findIndex(item => row.key === item.key)
  const item = newData[index]
  newData.splice(index, 1, {
    ...item,
    ...row,
  })
  setBayConfigurations(newData)
}

/**
 * Update checkbox state for a bay configuration in Forties
 */
export const updateFortyBayCheckbox = ({
  bayConfigurations,
  key,
  value,
  setBayConfigurations,
}) => {
  const newData = [...bayConfigurations]
  const index = newData.findIndex(item => item.key === key)
  if (index > -1) {
    newData[index] = {
      ...newData[index],
      fourtyFootOnly: value,
    }
    setBayConfigurations(newData)
  }
}

/**
 * Add a new bay configuration to Twenties
 */
export const addTwentyBay = ({
  setTwentiesConfigurations,
  setTwentiesCount,
  twentiesConfigurations,
  twentiesCount,
}) => {
  const newBay = {
    bay: '',
    hatchCovers: '2',
    key: twentiesCount,
  }
  setTwentiesConfigurations([...twentiesConfigurations, newBay])
  setTwentiesCount(twentiesCount + 1)
}

/**
 * Delete a bay configuration from Twenties
 */
export const deleteTwentyBay = ({
  key,
  setTwentiesConfigurations,
  twentiesConfigurations,
}) => {
  setTwentiesConfigurations(
    twentiesConfigurations.filter(item => item.key !== key)
  )
}

/**
 * Save changes to a bay configuration in Twenties
 */
export const saveTwentyBayConfig = ({
  row,
  setTwentiesConfigurations,
  twentiesConfigurations,
}) => {
  const newData = [...twentiesConfigurations]
  const index = newData.findIndex(item => row.key === item.key)
  const item = newData[index]
  newData.splice(index, 1, {
    ...item,
    ...row,
  })
  setTwentiesConfigurations(newData)
}
